'use client';

import type {
  BookAnotherProviderEventOptions,
  CachedLiveSlotsDiscrepancyEvent,
  CachedLiveSlotsDiscrepancyEventOptions,
  ClickedBookAnotherProviderEvent,
  ClickedProviderLinkEventOptions,
  ConversionProviderFields,
  ConversionSearchFilterFields,
  DTreeAnswerSubmittedEvent,
  DTreeQuestionShownEvent,
  EhrRedirectEventOptions,
  EventPayload,
  SchedulingEventOptions,
  StandardEventPayloadOptions,
} from './types';
import { pickBy } from 'lodash';
import { createLocationLabel } from '@provider-directory/functions/string';
import { deriveAgeBracket } from '@provider-directory/models/appointment.model';
import { getSpecialtySlugFromPathname } from '@provider-directory/models/navigation.model';
import {
  getPrimaryLocation,
  getProviderPrimarySpecialty,
} from '@provider-directory/models/provider.model';
import { sanitizeSearchParams } from '@provider-directory/services/providersSearch';
import { LocationResource } from '@provider-directory/types/locations.contracts';
import {
  AppointmentProvider,
  ProviderResource,
  ProvidersSearchCriteria,
} from '@provider-directory/types/providers.contracts';
import type { IsPrimaryCareSpecialtyFn } from '@provider-directory/types/search.model';
import { ProviderSpecificContext, SchedulingFlowType } from './conversions';

const stripEmpty = <T extends object>(payload: T): T =>
  pickBy(payload, (v) => v !== undefined && v !== null && v !== '') as T;

const formatHospitalGroup = (
  associations?: { name: string }[],
): string | undefined =>
  associations && associations.length > 0
    ? [...new Set(associations.map((h) => h.name))].sort().join(',')
    : undefined;

export const buildConversionProviderFields = ({
  provider,
  phoneNumber,
}: Pick<
  ProviderSpecificContext,
  'provider' | 'phoneNumber'
>): ConversionProviderFields => {
  const primarySpecialty = getProviderPrimarySpecialty(provider);

  const primaryLocation = getPrimaryLocation(provider.locations);

  const hospitalNames = formatHospitalGroup(provider.hospitalAssociations);

  return {
    pNPI: provider.npi,
    pName: provider.name.full,
    pSpecialty: primarySpecialty?.name,
    pGender: provider.gender ?? undefined,
    pLanguage:
      provider.languages.length > 0 ? provider.languages.join(',') : undefined,
    pLocation: primaryLocation
      ? createLocationLabel(primaryLocation.address)
      : undefined,
    pPhone: phoneNumber,
    pSchedulingType: provider.scheduling ?? undefined,
    pHospitalGroup: hospitalNames,
    pEmploymentStatus: provider.status ?? undefined,
  };
};

export const buildConversionSearchFilterFields = (
  sanitizedSearchCriteria: ProvidersSearchCriteria,
  originalSearchCriteria: Partial<ProvidersSearchCriteria>,
  filterSpecialtyDisplayName: string | undefined,
  isPrimaryCare: IsPrimaryCareSpecialtyFn,
): ConversionSearchFilterFields => {
  const fSpecialty =
    filterSpecialtyDisplayName ||
    getSpecialtySlugFromPathname(window.location.pathname);
  const fOnlineScheduling =
    sanitizedSearchCriteria.allowsOpenScheduling ||
    sanitizedSearchCriteria.hasSlots
      ? 'true'
      : undefined;
  const fPCP =
    sanitizedSearchCriteria.isPcp || isPrimaryCare(fSpecialty)
      ? 'true'
      : undefined;
  const fHospitalGroup = sanitizedSearchCriteria.hospitalAssociations;
  const fGender = sanitizedSearchCriteria.gender;
  const fLanguage = sanitizedSearchCriteria.languages?.toString();
  const fAgeGroup = sanitizedSearchCriteria.ageGroups?.toString();
  const fDistance = sanitizedSearchCriteria.maxDistance?.toString();
  const fLocation =
    originalSearchCriteria.address &&
    (sanitizedSearchCriteria.maxDistance ?? 0) > 0
      ? 'Yes'
      : 'No';

  return {
    fSpecialty,
    fOnlineScheduling,
    fPCP,
    fHospitalGroup,
    fGender,
    fLanguage,
    fAgeGroup,
    fLocation,
    fDistance,
  };
};

/**
 * Builds a flat payload combining provider fields, search filter fields,
 * and page context for behavioral analytics events.
 * Omits fields that are undefined/null/empty so payloads stay clean.
 */
export const buildEventPayload = (
  {
    provider,
    phoneNumber,
    searchCriteria,
    linkContextPage,
    linkContextComponent,
    clickText,
    clickURL,
    filterSpecialtyDisplayName,
    schedulingFlowType,
  }: StandardEventPayloadOptions,
  isPrimaryCare: IsPrimaryCareSpecialtyFn,
): EventPayload => {
  const base: Record<string, string | undefined> = {
    appType: 'provider-directory',
    linkContextPage,
    linkContextComponent,
    clickText,
    clickURL,
  };

  if (schedulingFlowType) {
    base.schedulingFlowType = schedulingFlowType;
  }

  if (provider) {
    const providerFields = buildConversionProviderFields({
      provider,
      phoneNumber,
    });
    Object.assign(base, providerFields);
  }

  if (searchCriteria) {
    const sanitized = sanitizeSearchParams(
      searchCriteria as ProvidersSearchCriteria,
      { units: 'miles' },
    );
    const filterFields = buildConversionSearchFilterFields(
      sanitized,
      searchCriteria,
      filterSpecialtyDisplayName,
      isPrimaryCare,
    );
    Object.assign(base, filterFields);
  }

  return stripEmpty(base);
};

export const buildDTreeQuestionShownPayload = ({
  questionId,
  provider,
  location,
  specialtyBranch,
  schedulingFlowType,
}: {
  questionId: string;
  provider: AppointmentProvider;
  location?: LocationResource;
  specialtyBranch?: string;
  schedulingFlowType?: SchedulingFlowType;
}): Partial<DTreeQuestionShownEvent> => {
  const primarySpecialty = getProviderPrimarySpecialty(provider);

  const hospitalNames = formatHospitalGroup(provider?.hospitalAssociations);

  const payload: DTreeQuestionShownEvent = {
    questionId,
    specialtyBranch,
    referringUrl: typeof window !== 'undefined' ? window.location.pathname : '',
    pNPI: provider?.npi ?? '',
    pGender: provider?.gender ?? undefined,
    pSpecialty: primarySpecialty?.name ?? '',
    pName: provider?.name.full ?? '',
    pLanguage:
      provider?.languages && provider.languages.length > 0
        ? provider.languages.join(',')
        : undefined,
    pLocation: location ? createLocationLabel(location.address) : undefined,
    pLocationId: location?.ehrExternalId ?? undefined,
    pHospitalGroup: hospitalNames,
    pEmploymentStatus: provider?.status ?? undefined,
    schedulingFlowType,
    appType: 'provider-directory',
  };

  return pickBy(payload, (v) => v);
};

export const buildDTreeAnswerSubmittedPayload = ({
  questionId,
  answer,
  provider,
  specialtyBranch,
  location,
  reasonForVisit,
  visitTypeId,
  schedulingFlowType,
}: {
  questionId: string;
  answer: string;
  provider: ProviderResource;
  location?: LocationResource;
  specialtyBranch?: string;
  reasonForVisit?: string;
  visitTypeId?: string;
  schedulingFlowType?: SchedulingFlowType;
}): Partial<DTreeAnswerSubmittedEvent> => {
  const primarySpecialty = getProviderPrimarySpecialty(provider);

  const hospitalNames = formatHospitalGroup(provider.hospitalAssociations);

  const payload: DTreeAnswerSubmittedEvent = {
    questionId,
    answer,
    specialtyBranch,
    referringUrl: typeof window !== 'undefined' ? window.location.pathname : '',
    pNPI: provider.npi,
    pGender: provider.gender ?? undefined,
    pSpecialty: primarySpecialty?.name ?? '',
    pName: provider.name.full,
    pLanguage:
      provider.languages.length > 0 ? provider.languages.join(',') : undefined,
    pLocation: location ? createLocationLabel(location.address) : undefined,
    pLocationId: location?.ehrExternalId ?? undefined,
    pHospitalGroup: hospitalNames,
    pEmploymentStatus: provider.status ?? undefined,
    schedulingFlowType,
    appType: 'provider-directory',
    reasonForVisit,
    visitTypeId,
  };

  return pickBy(payload, (v) => v);
};

export const buildBookAnotherProviderPayload = ({
  provider,
  searchCriteria,
  linkContextPage,
  linkContextComponent,
}: BookAnotherProviderEventOptions): Omit<
  ClickedBookAnotherProviderEvent,
  'event'
> => {
  const primarySpecialty = getProviderPrimarySpecialty(provider);
  const primaryLocation = getPrimaryLocation(provider.locations);

  const hospitalNames = formatHospitalGroup(provider.hospitalAssociations);

  const payload: Omit<ClickedBookAnotherProviderEvent, 'event'> = {
    appType: 'provider-directory',
    pNPI: provider.npi,
    pName: provider.name.full,
    pGender: provider.gender ?? undefined,
    pLanguage:
      provider.languages.length > 0 ? provider.languages.join(',') : undefined,
    pSpecialty: primarySpecialty?.name,
    pLocation: primaryLocation
      ? createLocationLabel(primaryLocation.address)
      : undefined,
    pSchedulingType: provider.scheduling ?? undefined,
    pHospitalGroup: hospitalNames,
    pEmploymentStatus: provider.status ?? undefined,
    schedulingFlowType: 'BookAnotherProvider',
    linkContextPage,
    linkContextComponent,
  };

  if (searchCriteria) {
    const sanitized = sanitizeSearchParams(
      searchCriteria as ProvidersSearchCriteria,
      { units: 'miles' },
    );

    payload.fSpecialty = sanitized.unified?.toString();
    payload.fOnlineScheduling =
      sanitized.allowsOpenScheduling || sanitized.hasSlots ? 'true' : undefined;
    payload.fPCP = sanitized.isPcp ? 'true' : undefined;
    payload.fHospitalGroup = sanitized.hospitalAssociations;
    payload.fGender = sanitized.gender;
    payload.fLanguage = sanitized.languages?.toString();
    payload.fAgeGroup = sanitized.ageGroups?.toString();
    payload.fDistance = sanitized.maxDistance?.toString();

    payload.fLocation =
      searchCriteria.address && (sanitized.maxDistance ?? 0) > 0 ? 'Yes' : 'No';
  }

  return stripEmpty(payload);
};

export const buildCachedLiveSlotsDiscrepancyPayload = ({
  provider,
  location,
  selectedDate,
  cachedSlotsCount,
  liveSlotsCount,
  reasonForVisit,
  linkContextPage,
  linkContextComponent,
}: CachedLiveSlotsDiscrepancyEventOptions): CachedLiveSlotsDiscrepancyEvent => {
  const primarySpecialty = getProviderPrimarySpecialty(provider);

  const payload: CachedLiveSlotsDiscrepancyEvent = {
    appType: 'provider-directory',
    pNPI: provider.npi,
    pName: provider.name.full,
    pSpecialty: primarySpecialty?.name,
    pGender: provider.gender ?? undefined,
    pLanguage:
      provider.languages.length > 0 ? provider.languages.join(',') : undefined,
    pLocation: location ? createLocationLabel(location.address) : undefined,
    pLocationId: location?.ehrExternalId ?? '',
    selectedDate,
    cachedSlotsCount,
    liveSlotsCount,
    reasonForVisit,
    linkContextPage,
    linkContextComponent,
  };

  return stripEmpty(payload);
};

export const buildEhrRedirectPayload = (
  {
    location,
    visitTypeId,
    redirectUrl,
    ...standardOptions
  }: EhrRedirectEventOptions,
  isPrimaryCare: IsPrimaryCareSpecialtyFn,
): EventPayload => {
  const payload = buildEventPayload(standardOptions, isPrimaryCare);

  if (location) {
    payload.pLocation = createLocationLabel(location.address);
    payload.pLocationId = location.ehrExternalId ?? undefined;
  }
  payload.visitTypeId = visitTypeId;
  payload.redirectUrl = redirectUrl;

  return stripEmpty(payload);
};

export const buildClickedProviderLinkPayload = (
  {
    mediaType,
    source,
    date,
    ...standardOptions
  }: ClickedProviderLinkEventOptions,
  isPrimaryCare: IsPrimaryCareSpecialtyFn,
): EventPayload => {
  const payload = buildEventPayload(standardOptions, isPrimaryCare);

  payload.mediaType = mediaType;
  payload.source = source;
  payload.date = date;

  return stripEmpty(payload);
};

export const buildSchedulingEventPayload = ({
  provider,
  appointment,
  schedulingFlowType,
  appointmentId,
  errorCode,
  linkContextPage,
  linkContextComponent,
}: SchedulingEventOptions): Record<string, unknown> => {
  const primarySpecialty = provider
    ? getProviderPrimarySpecialty(provider)
    : undefined;

  const primaryLocation = provider
    ? getPrimaryLocation(provider.locations)
    : undefined;

  const hospitalNames = provider
    ? formatHospitalGroup(provider.hospitalAssociations)
    : undefined;

  const payload: Record<string, unknown> = {
    appType: 'provider-directory',
    pNPI: provider?.npi,
    pName: provider?.name.full,
    pGender: provider?.gender ?? undefined,
    pLanguage:
      provider?.languages && provider.languages.length > 0
        ? provider.languages.join(',')
        : undefined,
    pSpecialty: primarySpecialty?.name,
    pLocation: primaryLocation
      ? createLocationLabel(primaryLocation.address)
      : undefined,
    pSchedulingType: provider?.scheduling ?? undefined,
    pHospitalGroup: hospitalNames,
    pEmploymentStatus: provider?.status ?? undefined,
    schedulingFlowType,
    linkContextPage,
    linkContextComponent,
    mSpecialtyBranch: appointment?.specialtyBranch ?? undefined,
    aInsurance: appointment?.usingInsurance ?? undefined,
    aTime:
      appointment?.slotDate && appointment?.slotTime
        ? `${appointment.slotDate} ${appointment.slotTime}`
        : undefined,
    aPatientSex: appointment?.legalSex ?? undefined,
    aPatientAgeBracket: deriveAgeBracket(appointment?.dob),
    aReasonForVisit: appointment?.reasonForVisit ?? undefined,
    visitTypeId: appointment?.visitTypeId ?? undefined,
    departmentId: appointment?.departmentId ?? undefined,
  };

  if (appointmentId) {
    payload.appointmentId = appointmentId;
  }
  if (errorCode) {
    payload.errorCode = errorCode;
  }

  return stripEmpty(payload);
};
